import {
  Box,
  Breadcrumbs,
  Card,
  CardActionArea,
  Container,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { CATEGORY_IMAGES } from "../assets/images";
import NoData from "../components/common/NoData";
import SEO from "../components/seo/SEO";
import { getCityCategories, getCityData } from "../utils/getCityData";
import FinalCTA from "./FinalCTA";
import ShareActions from "../components/common/ShareActions";

const CITY_BG_IMAGES = [
  CATEGORY_IMAGES.mandir,
  CATEGORY_IMAGES.tourist,
  CATEGORY_IMAGES.restaurant,
  CATEGORY_IMAGES.mall,
];

// fallback image (you can replace later category-wise)
const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1200&auto=format&fit=crop";

const City = () => {
  const { city } = useParams();
  const cityData = getCityData(city);
  const categories = getCityCategories(city);

  if (!cityData) {
    return (
      <NoData
        title="City not found"
        description="The city you are looking for is not available right now."
        showContact
      />
    );
  }
  return (
    <Box>
      <SEO
        title={`Best Places in ${cityData.name} | NearbyGuide`}
        description={`Discover trusted places, services and attractions in ${cityData.name}, ${cityData.state}. Explore temples, hospitals, schools, malls and more.`}
        canonical={`https://nearbyguide.in/city/${city}`}
        image={`https://nearbyguide.in/images/og-city.png`}
        key={`best places in ${cityData.name}, places to visit in ${cityData.name}, nearby places in ${cityData.name}, city guide ${cityData.name}, local places in ${cityData.name}, tourist places in ${cityData.name}, famous places in ${cityData.name}, restaurants in ${cityData.name}, cafes in ${cityData.name}, temples in ${cityData.name}, hospitals in ${cityData.name}, schools in ${cityData.name}, shopping malls in ${cityData.name}, parks in ${cityData.name}, things to do in ${cityData.name}, nearby attractions in ${cityData.name}, ${cityData.name} city guide, ${cityData.name} nearby places, ${cityData.name} local guide, NearbyGuide ${cityData.name}`}
      />

      <Box
        sx={{
          position: "relative",
          mb: { xs: 3, md: 4 },
          borderBottomLeftRadius: { xs: 28, md: 36 },
          borderBottomRightRadius: { xs: 28, md: 36 },
          overflow: "hidden",

          backgroundImage: `url(${cityData.headerImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* LEFT GRADIENT */}
        <Box
          sx={{
            background: {
              xs: "linear-gradient(180deg, rgba(13,59,102,0.75), rgba(13,59,102,0.4))",
              md: "linear-gradient(90deg, rgba(13,59,102,0.75) 0%, rgba(13,59,102,0.35) 55%, rgba(13,59,102,0.1) 100%)",
            },
            py: { xs: 2.5, md: 3.5 },
            px: { xs: 2, md: 4 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 560, color: "var(--color-sunshine)" }}>
              {/* ✅ FUNCTIONAL BREADCRUMBS */}
              <Breadcrumbs
                aria-label="breadcrumb"
                sx={{
                  mb: 1,
                  fontSize: "0.8rem",
                  color: "rgba(250,240,202,0.9)",
                  textTransform: "capitalize",
                }}
              >
                <MuiLink
                  component={Link}
                  to="/"
                  underline="hover"
                  color="inherit"
                >
                  Home
                </MuiLink>

                <MuiLink
                  component={Link}
                  to="/city"
                  underline="hover"
                  color="inherit"
                >
                  Cities
                </MuiLink>

                <Typography color="inherit">{cityData.name}</Typography>
              </Breadcrumbs>

              {/* TITLE */}
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: "2rem", md: "2.6rem" },
                  fontWeight: 900,
                  lineHeight: 1.1,
                  color: "#F4D35E",
                }}
              >
                {cityData.name}
              </Typography>

              {/* ACCENT */}
              <Box
                sx={{
                  width: 56,
                  height: 3,
                  bgcolor: "var(--color-bliss)",
                  borderRadius: 2,
                  mt: 1.2,
                  mb: 1.6,
                }}
              />

              {/* SUBTITLE */}
              <Typography
                sx={{
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  color: "rgba(250,240,202,0.95)",
                  lineHeight: 1.7,
                }}
              >
                Discover trusted places, services and attractions across{" "}
                <strong>{cityData.state}</strong> — curated to help you find
                what you need quickly.
              </Typography>
            </Box>
            <ShareActions
              title={`${cityData.name} | NearbyGuide`}
              text={`Discover trusted places and attractions in ${cityData.name}.`}
            />
          </Container>
        </Box>
      </Box>

      <Container sx={{ py: { xs: 4, md: 4 } }}>
        {/* ===== CATEGORY GRID (FLEXBOX) ===== */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {Object.keys(cityData).map((key) => {
            if (!Array.isArray(cityData[key])) return null;
            const categoryImage =
              CATEGORY_IMAGES[key] || CATEGORY_IMAGES.tourist;
            return (
              <Box
                key={key}
                sx={{
                  flex: {
                    xs: "1 1 100%", // 📱 mobile: 1 per row
                    sm: "1 1 calc(50% - 16px)",
                    md: "1 1 calc(20% - 20px)", // 🖥️ desktop: 5 per row
                  },
                  maxWidth: {
                    md: "calc(20% - 20px)",
                  },
                }}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: 220,
                    borderRadius: 1,
                    overflow: "hidden",
                    position: "relative",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 18px 44px rgba(0,0,0,0.22)",
                    },
                  }}
                >
                  <CardActionArea
                    component={Link}
                    to={`/city/${city}/${key}`}
                    sx={{ height: "100%" }}
                  >
                    {/* IMAGE */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `url(${categoryImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center top",
                      }}
                    />

                    {/* DARK OVERLAY */}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.75))",
                      }}
                    />

                    {/* TEXT */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 14,
                        left: 14,
                        right: 14,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#FFFFFF",
                          fontWeight: 700,
                          fontSize: "1rem",
                          textTransform: "capitalize",
                        }}
                      >
                        {key.replace("-", " ")}
                      </Typography>

                      <Typography
                        sx={{
                          color: "rgba(255,255,255,0.85)",
                          fontSize: "0.75rem",
                          mt: 0.3,
                        }}
                      >
                        {cityData[key].length} places available
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Box>
            );
          })}
        </Box>
      </Container>
      <FinalCTA />
    </Box>
  );
};

export default City;
